// src/components/shipment/MoreFiltersDrawer.jsx
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SlidersHorizontal } from 'lucide-react';
import { SearchBarPagination } from '../SearchBox/SearchBarPagination';

const MoreFiltersDrawer = ({
    isOpen,
    onApply,
    carrierFilter,
    setCarrierFilter,
    uniqueCarriers,
    polFilter,
    setPolFilter,
    podFilter,
    setPodFilter,
    dateRange,
    setDateRange,
    activeFiltersCount,
    clearAllFilters
}) => {
    if (!isOpen) return null;


    const _getPortCode = (strdata) => {
        if (!strdata) return "";
        const match = strdata.match(/\[(.*?)\]/);
        const code = match ? match[1] : "";
        // console.log(code); // INBOM
        return code;
    };

    return (
        <AnimatePresence>
            <motion.div
                id="shipment-more-filters-drawer"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                className="absolute top-full left-0 mt-2 w-80 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 z-50 overflow-visible"
            >
                <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                    <h3 className="font-semibold text-slate-800 dark:text-white">Advanced Filters</h3>
                    {activeFiltersCount > 0 && (
                        <button onClick={clearAllFilters} className="text-xs text-blue-600 dark:text-blue-400 hover:underline">
                            Clear All
                        </button>
                    )}
                </div>

                <div className="p-4 space-y-4">
                    {/* Carrier Filter */}
                    <div>
                        <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wider">Carrier</label>
                        <select
                            value={carrierFilter}
                            onChange={(e) => setCarrierFilter(e.target.value)}
                            className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-sm text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                        >
                            <option value="all">All Carriers</option>
                            {uniqueCarriers.map(carrier => (
                                <option key={carrier} value={carrier.CarrierSCAC}>{carrier.CarrierName}</option>
                            ))}
                        </select>
                    </div>

                    {/* POL Filter */}
                    <div>
                        <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wider">Port of Loading (POL)</label>
                        <SearchBarPagination
                            searchAPI="v1/AutoSuggestion/GetDestinationSuggestion?Prefix="
                            paramCompanyId="companyId"
                            selectReacord={(d) => setPolFilter(_getPortCode(d?.[1] ?? ""))}
                            value={polFilter}
                            placeholder="e.g., Los Angeles, Shanghai"
                            showResults={1}
                            onchange={(val) => {
                                if (!val?.trim()) setPolFilter("");
                            }}
                            className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-sm text-slate-800 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                            dropdownZIndex={10050}
                        />
                        {/* <input
                            type="text"
                            value={polFilter}

                            onChange={(e) => setPolFilter(e.target.value)}
                            placeholder="e.g., Los Angeles, Shanghai"
                            className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-sm text-slate-800 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                        /> */}
                    </div>

                    {/* POD Filter */}
                    <div>
                        <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wider">Port of Discharge (POD)</label>
                        {/* <input
                            type="text"
                            value={podFilter}
                            onChange={(e) => setPodFilter(e.target.value)}
                            placeholder="e.g., Rotterdam, Sydney"
                            className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-sm text-slate-800 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                        /> */}
                        <SearchBarPagination
                            searchAPI="v1/AutoSuggestion/GetDestinationSuggestion?Prefix="
                            paramCompanyId="companyId"
                            selectReacord={(d) => setPodFilter(_getPortCode(d?.[1] ?? ""))}
                            value={podFilter}
                            placeholder="e.g., Rotterdam, Sydney"
                            showResults={1}
                            onchange={(val) => {
                                if (!val?.trim()) setPodFilter("");
                            }}
                            className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-sm text-slate-800 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                            dropdownZIndex={10050}
                        />
                    </div>

                    {/* Date Range */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wider">Date From</label>
                            <input
                                type="date"
                                value={dateRange.from}
                                onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                                className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-sm text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wider">Date To</label>
                            <input
                                type="date"
                                value={dateRange.to}
                                onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                                className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-sm text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                            />
                        </div>
                    </div>
                </div>

                <div className="p-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
                    <p className="text-xs text-slate-500 dark:text-slate-400">{activeFiltersCount} filter(s) applied</p>
                    <button onClick={onApply} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors">
                        Apply Filters
                    </button>
                </div>
            </motion.div>
        </AnimatePresence>
    );
};

export default MoreFiltersDrawer;